import React from 'react'
import { useTrackMutation } from '@mote/api'
import { useCountdown } from '@mote/helpers'
import { isSafari } from '@mote/helpers/browser'
import { TRACK_EVENT_NAMES } from '@mote/helpers/constants'
import { useFlightRecorder } from '@mote/helpers/contexts'
import { DiagnosticsLogger } from '@mote/helpers/diagnostics'
import { useAudioStream } from '@mote/helpers/hooks/audio'
import {
  useMicrophone,
  useMicrophonePermission
} from '@mote/helpers/hooks/microphone'
import { useRecorder } from '@mote/helpers/hooks/recorder'
import { Block, Spinner, Text } from '@mote/ui'
import styled, { useTheme } from '@xstyled/styled-components'
import {
  CancelRecordingButton,
  CompleteRecordingButton,
  ControlsContainer,
  Emoji,
  RecordingTime,
  ResumePauseRecordingButton,
  StartRecordingButton
} from '../../../shared/components'
import { Loader } from '../shared/Loader'
import { Notice } from '../shared/Notice'
import { textDefaultProps } from '../shared/RecorderText'
import { Error, Microphone } from './icons'

const logD = new DiagnosticsLogger('webRecorder')

/**
 * @deprecated
 */
export const WebRecorder = ({ parentMoteId, onFinalize, dataTestId }) => {
  const theme = useTheme()
  const {
    start,
    stop,
    pause,
    resume,
    cancel,
    status,
    error: recorderError,
    cleanup,
    uploaderError,
    registeredMote,
    finalizeRecording,
    finalizeData
  } = useRecorder({
    parentMoteId
  })
  const [seconds, { start: startTimer, stop: stopTimer, reset: resetTimer }] =
    useCountdown({ seconds: 30, lowerBoundaryLimit: 0 })

  const {
    start: startAudioStream,
    stop: stopAudioStream,
    pause: pauseAudioStream,
    resume: resumeAudioStream,
    audioContext,
    audioStream
  } = useAudioStream()
  const { permissionStatus, permissionResponseReceived } =
    useMicrophonePermission()
  const { askForMicrophoneAccess } = useMicrophone()
  const [microphoneError, setMicrophoneError] = React.useState(null)
  const recordingInitiated = React.useRef(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [stoppingDeferred, setStoppingDeferred] = React.useState(false)
  const { chunksInFlight } = useFlightRecorder()

  const { mutate: track } = useTrackMutation()
  const shouldErrorOut = microphoneError || recorderError || uploaderError

  const resetError = () => {
    setError(null)
    setMicrophoneError(null)
    cleanup({ flushOldData: true })
  }

  const trackMicrophoneEvent = eventName => {
    track({
      variables: {
        event: TRACK_EVENT_NAMES.permission.microphone[eventName]
      }
    })
  }

  const checkMicPermission = emitEvent => {
    if (permissionStatus === 'prompt') {
      setMicrophoneError({ name: 'PromptForPermission' })

      if (emitEvent) {
        trackMicrophoneEvent('requestTriggered')
      }

      return false
    }

    if (permissionStatus === 'denied') {
      setMicrophoneError({ name: 'NotAllowedError' })

      if (emitEvent) {
        trackMicrophoneEvent('requestBlocked')
      }

      return false
    }

    if (error) resetError()

    return true
  }

  const promptForMicPermission = async () => {
    setMicrophoneError(prev => ({ ...prev, loading: true }))
    trackMicrophoneEvent('requestTriggered')
    const micError = await askForMicrophoneAccess()

    if (micError) {
      if (micError.name === 'NotAllowedError') {
        trackMicrophoneEvent('requestBlocked')
      }
      setMicrophoneError({ ...micError, loading: false })
      return
    }

    resetError()
  }

  const startRecording = async () => {
    if (recordingInitiated.current) {
      console.log('recording already initiated')
      return
    }

    if (!isSafari()) {
      if (!permissionResponseReceived) return

      const shouldProceed = checkMicPermission(true)

      if (!shouldProceed) return
    }

    recordingInitiated.current = true
    startTimer()

    const streamPromise = start()

    streamPromise.then(response => {
      if (!(response instanceof MediaStream) && recordingInitiated.current) {
        cancelRecording()
        setError({ code: 'NotFoundError' })
        return
      }

      if (!isSafari()) startAudioStream(response)
    })

    track({ variables: { event: TRACK_EVENT_NAMES.mote.record.start } })
  }

  const pauseRecording = () => {
    stopTimer()
    pause()
    if (!isSafari()) pauseAudioStream()
  }

  const resumeRecording = () => {
    startTimer()
    resume()
    if (!isSafari()) resumeAudioStream()
  }

  const stopRecording = async () => {
    resetTimer()
    if (!isSafari()) stopAudioStream(true)
    recordingInitiated.current = false

    const conditionsMet = await stop({ disableAutoDebit: true })
    if (!conditionsMet) {
      logD.info('Deferring')
      setStoppingDeferred(true)
    }
  }

  const cancelRecording = () => {
    recordingInitiated.current = false
    resetTimer()
    cancel({
      flushOldData: !shouldErrorOut
    })
    if (!isSafari()) stopAudioStream(true)
  }

  React.useEffect(() => {
    if (!permissionResponseReceived) return
    if (!isLoading) return

    setIsLoading(false)
    checkMicPermission(false)
  }, [permissionResponseReceived, isLoading])

  React.useEffect(() => {
    if (seconds > 0) return
    stopRecording()
  }, [seconds])

  React.useEffect(() => {
    if (!shouldErrorOut) {
      if (error) setError(null)

      return
    }

    setError(shouldErrorOut)
    recordingInitiated.current = false
    cancelRecording()
  }, [shouldErrorOut, error])

  React.useEffect(() => {
    if (!stoppingDeferred || error) return
    if (!registeredMote) return
    if (chunksInFlight) return
    logD.info('Triggering finalize')

    finalizeRecording({ disableAutoDebit: true })
    setStoppingDeferred(false)
  }, [chunksInFlight, stoppingDeferred, error, registeredMote])

  React.useEffect(() => {
    if (!finalizeData) return
    onFinalize(finalizeData.moteStatus)
  }, [finalizeData])

  if (error !== null) {
    let ErrorComponent = (
      <Notice
        Icon={Error}
        title='Oh no!'
        description='We encountered an unexpected error whilst recording your mote. Please try again.'
        cta={{ action: resetError }}
      />
    )

    switch (error?.name) {
      case 'NotAllowedError': {
        ErrorComponent = (
          <Notice
            Icon={Microphone}
            title='Oh no!'
            description='Please check that access to your microphone is allowed for this website and try again.'
            cta={{ action: resetError }}
          />
        )
        break
      }

      case 'PromptForPermission': {
        ErrorComponent = error.loading ? (
          <Loader title='Waiting for permission...' hint={null} />
        ) : (
          <Notice
            Icon={Microphone}
            title='Permission Required!'
            description='We need to access to your microphone in order to record your mote. Please click the button below to give permission.'
            cta={{
              action: promptForMicPermission,
              text: 'Give permission'
            }}
          />
        )
        break
      }

      case 'NotFoundError': {
        ErrorComponent = (
          <Notice
            Icon={Microphone}
            title='Microphone missing'
            description='We were unable to detect your microphone, please check your connection and try again.'
            cta={{ action: resetError }}
          />
        )
        break
      }

      case 'MOTE_ID_UNDEFINED_ERROR': {
        ErrorComponent = (
          <Notice
            Icon={Error}
            title='Recording unsuccessful'
            description='We were unable to complete the recording. Please check your internet connection and try again or kindly contact support.'
            cta={{ action: resetError }}
          />
        )
        break
      }

      default:
        break
    }

    return (
      <Container align='center' justify='center'>
        {ErrorComponent}
      </Container>
    )
  }

  return (
    <Container
      align='center'
      justify='center'
      gap={32}
      data-testid={dataTestId}
    >
      <ControlsContainer gap={32}>
        {(status.isRecording || status.isPaused) && (
          <CancelRecordingButton onClick={cancelRecording} />
        )}

        {status.isIdle && (
          <StartRecordingButton
            ref={btn => btn?.focus()}
            onClick={startRecording}
          />
        )}

        {!status.isIdle && (
          <>
            {status.isStopped ? (
              <Spinner radius={100} duration={1400} strokeWidth={3} />
            ) : (
              <CompleteRecordingButton
                audioContext={audioContext}
                audioStream={audioStream}
                onClick={30 - seconds > 0 ? stopRecording : null}
                backgroundColor={
                  30 - seconds > 0
                    ? theme.colors.primary.base
                    : theme.colors.primary.l2
                }
              />
            )}
          </>
        )}

        {(status.isRecording || status.isPaused) && (
          <ResumePauseRecordingButton
            isRecording={status.isRecording}
            disabled={isSafari()}
            tooltipProps={{
              content: isSafari()
                ? 'Disabled on Safari'
                : status.isRecording
                ? 'Pause'
                : 'Resume'
            }}
            onClick={
              !isSafari()
                ? status.isRecording
                  ? pauseRecording
                  : resumeRecording
                : null
            }
            forceShowTooltip={isSafari()}
          />
        )}
      </ControlsContainer>

      <Block gap={8}>
        {status.isIdle && (
          <Text {...textDefaultProps}>
            Click{' '}
            <Emoji
              label='Pointing finger'
              emojiSize={20}
              fontSize={20}
              noTooltip
            >
              ☝️
            </Emoji>{' '}
            to start
          </Text>
        )}

        {status.isRecording && (
          <RecordingTime
            fontSize={20}
            fontWeight={500}
            dotSize={10}
            gap={8}
            seconds={seconds}
          />
        )}

        {status.isStopped && <Text {...textDefaultProps}>Uploading...</Text>}

        {status.isPaused && <Text {...textDefaultProps}>Paused</Text>}
      </Block>
    </Container>
  )
}

const Container = styled(Block)`
  padding-top: 28px;
  box-sizing: border-box;
  outline: none !important;
`
